import { authExchange } from '@urql/exchange-auth';

import { isPossibleAuthError } from './auth-error';
import type { SessionService } from './session-service';

/**
 * Teaches urql what an expired session looks like (PSTUDIO-4022).
 *
 * The gateway does not fail requests once the Kratos cookie is gone — the Hasura
 * auth webhook downgrades the role to `anonymous`, so the app gets HTTP 200 with a
 * `validation-failed` error. `didAuthError` therefore only raises a suspicion; the
 * session service asks Kratos, whose 401 is the actual arbiter. urql retries each
 * operation at most once (it tags retries with `authAttempt`), so a genuinely dead
 * session surfaces the original error instead of looping.
 */
export function createSessionAuthExchange(session: SessionService) {
  return authExchange(async (utils) => {
    // Warm the id so the very first operation can carry it.
    await session.getIdentityId();

    return {
      addAuthToOperation(operation) {
        const identityId = session.identityId();

        if (!identityId) return operation;

        return utils.appendHeaders(operation, {
          'x-hasura-user-id': identityId,
        });
      },
      didAuthError(error) {
        return isPossibleAuthError(error);
      },
      async refreshAuth() {
        // Never throws: urql blocks the whole queue on a rejected refreshAuth.
        await session.revalidate();
      },
    };
  });
}
